import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  // withCredentials intentionally omitted (defaults to false).
  // Auth is handled via Bearer token in localStorage (set by the request
  // interceptor below). Using withCredentials: true causes CORS preflight
  // failures on custom domains (filmistani.com) because the Kubernetes ingress
  // returns Access-Control-Allow-Origin: * on OPTIONS — which is incompatible
  // with withCredentials per the CORS spec. Browsers reject the preflight,
  // so every cross-origin API call silently fails (login, /auth/me, etc.).
  // The session cookie is still set by the backend for same-origin fallback,
  // but Bearer token is the primary and more reliable auth mechanism.
});

// ---- Request scheduler (frontend-side concurrency cap) ----
// Production container is single-worker and saturates if we fire 20+ parallel
// requests at boot (returns 502s). We queue our GETs through a tiny scheduler
// that caps in-flight to MAX_INFLIGHT — flushing as each finishes.
const MAX_INFLIGHT = 6;
let inflight = 0;
const queue = [];
const acquire = () => new Promise((resolve) => {
  if (inflight < MAX_INFLIGHT) {
    inflight++;
    resolve();
  } else {
    queue.push(resolve);
  }
});
const release = () => {
  inflight--;
  const next = queue.shift();
  if (next) {
    inflight++;
    next();
  }
};

api.interceptors.request.use(async (config) => {
  // Only throttle GETs — POST/PUT/DELETE are user-driven and rare.
  // v2.20 — Navigation-critical routes (/title/, /similar/) bypass the
  // 6-slot throttle so a click-to-detail never queues behind background
  // Home SWR refreshes. Without this, MovieDetails' lower half (synopsis,
  // cast, providers, more-like-this) appears stuck for several seconds
  // because the GET is parked in the queue while 10+ rail fetches drain.
  const url = config.url || "";
  const isPriority = url.startsWith("/title/") || url.startsWith("/similar/") || url.startsWith("/anticipated-india");
  if ((config.method || "get").toLowerCase() === "get" && !isPriority) {
    await acquire();
    config._throttled = true;
  }
  const t = localStorage.getItem("fm_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // Anonymous browser ID — lets backend personalize home rails for guests
  // without forcing sign-in. Generated lazily on first call.
  let anon = localStorage.getItem("fm_anon");
  if (!anon) {
    anon = "anon_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
    try { localStorage.setItem("fm_anon", anon); } catch (e) { /* private mode */ }
  }
  config.headers["X-Anon-Id"] = anon;
  // Attach include_adult flag based on user's 18+ toggle
  const adult = localStorage.getItem("fm_adult") === "1";
  if (adult) {
    config.params = { ...(config.params || {}), include_adult: true };
  }
  return config;
});

api.interceptors.response.use(
  (resp) => {
    if (resp.config && resp.config._throttled) release();
    return resp;
  },
  (err) => {
    if (err.config && err.config._throttled) release();
    // Auto-clear stale/expired token from localStorage on 401 so the next
    // hard refresh shows the login button instead of a stuck "loading" state.
    // Ignore 401s on /auth/ routes (those are expected during login flow).
    const url = err.config?.url || "";
    if (err?.response?.status === 401 && !url.startsWith("/auth/")) {
      try { localStorage.removeItem("fm_token"); } catch (_e) { /* noop */ }
    }
    return Promise.reject(err);
  }
);

// ---- Title-details prefetch (warm backend cache before user clicks) ----
// Fired when a card is hovered (desktop) or enters the viewport during idle
// time (mobile). By the time the user clicks, the L1/L2 cache is hot and the
// detail page loads instantly. Dedup'd locally so we never refire for the
// same title in the same session.
const _prefetchedTitles = new Set();
export const prefetchTitleDetails = (mediaType, tmdbId) => {
  if (!mediaType || !tmdbId) return;
  const key = `${mediaType}-${tmdbId}`;
  if (_prefetchedTitles.has(key)) return;
  _prefetchedTitles.add(key);
  const run = () => {
    // Fire directly (bypass axios scheduler) to keep it OUT of the GET queue
    // so it never delays user-initiated requests.
    fetch(`${API}/title/${mediaType}/${tmdbId}`, {
      method: "GET",
      credentials: "include",
      headers: { "X-Prefetch": "1" },
    }).catch(() => { _prefetchedTitles.delete(key); });
  };
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 200);
  }
};


// ── v4.0 Frontend GET response cache + in-flight deduplication ────────────
// Prevents duplicate network calls for the same URL and makes page revisits instant.
const _CACHEABLE = ["/title/","/similar/","/collections/","/search","/where-to-watch/","/platform/","/collection/","/home-secondary-bundle","/home-bundle","/home-priority-bundle"];
const _NO_CACHE  = ["/me/","/auth/","/admin/","/social/","/games/","/iq/"];
const _FE_CACHE  = new Map(); // cacheKey → { ts, data }
const _FE_TTL    = 120_000;  // 2 minutes

export const isCacheableUrl = (url = "") => {
  if (_NO_CACHE.some(p => url.includes(p))) return false;
  return _CACHEABLE.some(p => url.includes(p));
};
const _ck = (cfg) => `${cfg.url}||${JSON.stringify(cfg.params || {})}`;

export const getFECache = (url, params) => {
  const key = `${url}||${JSON.stringify(params || {})}`;
  const hit = _FE_CACHE.get(key);
  return hit && (Date.now() - hit.ts) < _FE_TTL ? hit.data : null;
};
export const setFECache = (url, params, data) => {
  if (!isCacheableUrl(url)) return;
  _FE_CACHE.set(`${url}||${JSON.stringify(params || {})}`, { ts: Date.now(), data });
};

// Hook into response interceptor to populate FE cache
api.interceptors.response.use(
  (resp) => {
    if (isCacheableUrl(resp.config?.url || "")) {
      _FE_CACHE.set(_ck(resp.config), { ts: Date.now(), data: resp.data });
    }
    return resp;
  },
  (err) => Promise.reject(err)
);
